import type { Itinerary, ItineraryBlock, CostEstimate } from "../../shared/types";

const CATEGORY_ICON: Record<string, string> = {
  food: "🍽️",
  sight: "📸",
  activity: "🎟️",
  transit: "🚆",
  rest: "🌿",
  accommodation: "🏨",
  shopping: "🛍️",
  nightlife: "🌙",
};

const TIME_LABEL: Record<string, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
};

function money(c?: CostEstimate | null): string {
  if (!c || !c.amount) return "Free";
  return `${c.currency} ${Math.round(c.amount)}/${c.per === "group" ? "group" : "pp"}`;
}

function Block({ block }: { block: ItineraryBlock }) {
  return (
    <div className="relative pl-6">
      <span className="absolute left-0 top-1.5 text-base leading-none">{CATEGORY_ICON[block.category] || "•"}</span>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <p className="font-semibold text-ink">{block.title}</p>
        <span className="whitespace-nowrap text-xs font-medium text-slate-500">{money(block.estCost)}</span>
      </div>
      {block.description && <p className="mt-0.5 text-sm text-slate-600">{block.description}</p>}
      {block.bookingHint && (
        <p className="mt-1 text-xs text-sun">💡 {block.bookingHint}</p>
      )}
      {block.matchesPreferences && block.matchesPreferences.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {block.matchesPreferences.map((m, i) => (
            <span key={i} className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
              ✓ {m}
            </span>
          ))}
        </div>
      )}
      {block.searchLinks && block.searchLinks.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {block.searchLinks.map((l) => (
            <a
              key={l.url}
              href={l.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-brand-50 hover:text-brand-700"
            >
              {l.label} ↗
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// Collapse consecutive same-city days into a route like Kunming → Dali → Lijiang.
function deriveRoute(itinerary: Itinerary): { city: string; days: number }[] {
  const out: { city: string; days: number }[] = [];
  for (const d of itinerary.days) {
    const city = d.location;
    if (!city) continue;
    const last = out[out.length - 1];
    if (last && last.city === city) last.days += 1;
    else out.push({ city, days: 1 });
  }
  return out;
}

export function ItineraryView({ itinerary }: { itinerary: Itinerary }) {
  const b = itinerary.budget;
  const route = deriveRoute(itinerary);
  return (
    <div className="space-y-5">
      {itinerary.summary && (
        <div className="card bg-gradient-to-br from-brand-50 to-white">
          <p className="text-[15px] leading-relaxed text-slate-700">{itinerary.summary}</p>
        </div>
      )}

      {route.length > 1 && (
        <div className="card">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">Your route</h3>
          <div className="flex flex-wrap items-center gap-1.5">
            {route.map((r, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="rounded-lg bg-brand-50 px-3 py-1.5 text-sm font-semibold text-brand-700">
                  {r.city} <span className="font-normal text-brand-400">· {r.days}d</span>
                </span>
                {i < route.length - 1 && <span className="text-slate-300">→</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Budget + conflicts side panel */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="card">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">Estimated budget</h3>
          <dl className="space-y-1.5 text-sm">
            {[
              ["Lodging", b.lodging],
              ["Activities", b.activities],
              ["Food", b.food],
              ["Transport", b.transport],
              ["Misc", b.misc],
            ]
              .filter(([, v]) => typeof v === "number" && (v as number) > 0)
              .map(([k, v]) => (
                <div key={k as string} className="flex justify-between">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-medium">{b.currency} {Math.round(v as number)}</dd>
                </div>
              ))}
            <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 font-semibold">
              <dt>Total estimate</dt>
              <dd>{b.currency} {b.totalEstimate ? Math.round(b.totalEstimate) : "—"}</dd>
            </div>
            {typeof b.perPersonEstimate === "number" && (
              <div className="flex justify-between text-slate-500">
                <dt>Per person</dt>
                <dd>{b.currency} {Math.round(b.perPersonEstimate)}</dd>
              </div>
            )}
          </dl>
          {b.withinBudget != null && (
            <p
              className={`mt-3 rounded-lg px-3 py-2 text-xs font-medium ${
                b.withinBudget ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
              }`}
            >
              {b.withinBudget ? "✓ Fits within your stated budget" : "⚠ Over your stated budget — trim or adjust style"}
            </p>
          )}
          {b.note && <p className="mt-2 text-xs text-slate-400">{b.note}</p>}
        </div>

        <div className="card">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">
            How we balanced the group
          </h3>
          <ul className="space-y-2 text-sm text-slate-600">
            {itinerary.conflictsFlagged.map((c, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-brand-500">⚖️</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Day-by-day */}
      <div className="space-y-4">
        {itinerary.days.map((day) => (
          <div key={day.dayNumber} className="card">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-lg font-bold text-ink">
                <span className="mr-2 rounded-lg bg-brand-600 px-2 py-0.5 text-sm text-white">Day {day.dayNumber}</span>
                {day.title}
              </h3>
              <div className="flex items-center gap-2 text-right text-xs text-slate-400">
                {day.location && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500">📍 {day.location}</span>
                )}
                <div>
                  {day.date && <div>{day.date}</div>}
                  {day.dailyCostEstimate && <div>~{money(day.dailyCostEstimate)}</div>}
                </div>
              </div>
            </div>
            {day.summary && <p className="mb-3 text-sm text-slate-500">{day.summary}</p>}
            <div className="space-y-4">
              {(["morning", "afternoon", "evening"] as const).map((slot) => {
                const blocks = day.blocks.filter((x) => x.time === slot);
                if (!blocks.length) return null;
                return (
                  <div key={slot}>
                    <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                      {TIME_LABEL[slot]}
                    </p>
                    <div className="space-y-3">
                      {blocks.map((block, i) => (
                        <Block key={i} block={block} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Tips */}
      <div className="grid gap-4 md:grid-cols-2">
        {itinerary.packingTips.length > 0 && (
          <div className="card">
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">Packing tips</h3>
            <ul className="list-inside list-disc space-y-1 text-sm text-slate-600">
              {itinerary.packingTips.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </div>
        )}
        {itinerary.localTips.length > 0 && (
          <div className="card">
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">Local tips</h3>
            <ul className="list-inside list-disc space-y-1 text-sm text-slate-600">
              {itinerary.localTips.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
